import { t } from "@/i18n";

import type { FieldErrors } from "./queries";

/**
 * The rules of the card index, apart from the components that draw it.
 *
 * The server is the authority on what a card must be (`CardSerializer`); what
 * is here only lets the table mode mark an empty name or a broken address the
 * moment it is typed, instead of after *Guardar*.
 */

/** The table mode's columns, in the order a card is read. */
export const GRID_COLUMNS = [
  "name",
  "email",
  "business",
  "phone",
  "mobile",
  "job",
  "trade_show",
  "edition",
  "city",
  "website",
  "address",
  "neighborhood",
  "postal_code",
  "phone2",
] as const;

export type GridColumn = (typeof GRID_COLUMNS)[number];

/** One row of the table: every cell is text, as Excel's are. */
export type GridRow = { key: number } & Record<GridColumn, string>;

export const REQUIRED_COLUMNS: readonly GridColumn[] = ["name", "email", "business"];

/** The model's limits, so a cell cannot hold what the server would refuse. */
export const MAX_LENGTH: Record<GridColumn, number> = {
  name: 200,
  email: 200,
  business: 250,
  phone: 30,
  mobile: 30,
  phone2: 30,
  job: 100,
  trade_show: 120,
  edition: 4,
  city: 150,
  website: 100,
  address: 200,
  neighborhood: 100,
  postal_code: 10,
};

export function emptyRow(key: number): GridRow {
  const row = { key } as GridRow;
  for (const column of GRID_COLUMNS) row[column] = "";
  return row;
}

export function isBlank(row: GridRow): boolean {
  return GRID_COLUMNS.every((column) => row[column].trim() === "");
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS = /\d/g;
/** A Mexican number with its lada. The server accepts more; this is the floor. */
const PHONE_DIGITS = 10;

/** What can be said about a row without asking the server. */
export function quickCheck(row: GridRow): FieldErrors {
  const errors: FieldErrors = {};
  for (const column of REQUIRED_COLUMNS) {
    if (!row[column].trim()) errors[column] = [t("cards.check.required")];
  }
  const email = row.email.trim();
  if (email && !EMAIL.test(email)) errors.email = [t("cards.check.email")];
  for (const column of ["phone", "mobile", "phone2"] as const) {
    const digits = row[column].match(DIGITS)?.length ?? 0;
    if (digits > 0 && digits < PHONE_DIGITS) errors[column] = [t("cards.check.phone")];
  }
  const edition = row.edition.trim();
  if (edition && !/^\d{4}$/.test(edition)) errors.edition = [t("cards.check.edition")];
  const postalCode = row.postal_code.trim();
  if (/^\d+$/.test(postalCode) && postalCode.length !== 5) {
    errors.postal_code = [t("cards.check.postalCode")];
  }
  return errors;
}

/**
 * Excel's clipboard: tab between cells, newline between rows, and a cell that
 * holds either of those — or a quote — wrapped in quotes with its own quotes
 * doubled. The trailing newline Excel always adds is not a row.
 */
export function parseClipboard(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        cell += '"';
        index++;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"' && cell === "") {
      quoted = true;
    } else if (char === "\t") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[index + 1] === "\n") index++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.map((cells) => cells.map((value) => value.trim()));
}

/**
 * Lay a pasted block over the table, its top-left cell at the one pasted into,
 * adding rows below as the block needs them. Cells past the last column are
 * dropped, as Excel would refuse them.
 */
export function pasteBlock(
  rows: readonly GridRow[],
  block: string[][],
  at: { row: number; column: number },
  nextKey: () => number,
): GridRow[] {
  const next = rows.map((row) => ({ ...row }));
  block.forEach((cells, offset) => {
    const index = at.row + offset;
    while (next.length <= index) next.push(emptyRow(nextKey()));
    const target = next[index];
    if (!target) return;
    cells.forEach((value, step) => {
      const column = GRID_COLUMNS[at.column + step];
      if (column) target[column] = value.slice(0, MAX_LENGTH[column]);
    });
  });
  return next;
}

/** The years the *Edición* picker offers: next year back to the index's first. */
export function editionYears(today = new Date()): number[] {
  const years: number[] = [];
  for (let year = today.getFullYear() + 1; year >= 2017; year--) years.push(year);
  return years;
}
